import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { COMPARISONS, findComparison } from '@/content/comparisons';
import { safeJsonLd } from '@/lib/jsonLd';
import { CheckCircle, XCircle, ArrowRight } from '@phosphor-icons/react';

const SITE = (process.env.REACT_APP_SITE_URL || 'https://goconvertdocuments.com').replace(/\/$/, '');

export default function Compare() {
  const { slug } = useParams();
  const c = findComparison(slug);

  useEffect(() => {
    if (!c) return;
    document.title = `${c.title} | Free PDF Converter Comparison 2026`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', c.description);
    const kw = document.querySelector('meta[name="keywords"]');
    if (kw) kw.setAttribute('content', c.keywords);
  }, [c]);

  if (!c) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <p className="font-display text-3xl">Comparison not found</p>
        <Link to="/" className="brutal-btn mt-6 inline-block">← Back home</Link>
      </div>
    );
  }

  const others = COMPARISONS.filter((x) => x.slug !== c.slug);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">Comparison</p>
      <h1
        data-testid="compare-title"
        className="font-display font-bold text-4xl md:text-6xl leading-tight text-slate-950 mt-2"
      >
        {c.title}
      </h1>
      <p className="text-lg text-zinc-700 mt-6 leading-relaxed max-w-3xl">{c.intro}</p>

      <div className="mt-10 overflow-x-auto">
        <table
          data-testid="compare-table"
          className="w-full border-2 border-black bg-white brutal-shadow"
        >
          <thead className="bg-black text-white">
            <tr>
              <th className="text-left font-display px-4 py-3 text-sm">Feature</th>
              <th className="text-left font-display px-4 py-3 text-sm">{c.competitor}</th>
              <th className="text-left font-display px-4 py-3 text-sm bg-yellow-300 text-black">
                Go Convert Documents
              </th>
            </tr>
          </thead>
          <tbody>
            {c.rows.map((row) => (
              <tr key={row[0]} className="border-t-2 border-black">
                <td className="px-4 py-3 text-sm font-semibold">{row[0]}</td>
                <td className="px-4 py-3 text-sm text-zinc-700">{row[1]}</td>
                <td className="px-4 py-3 text-sm text-slate-950 bg-yellow-50 font-medium">{row[2]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-12 grid md:grid-cols-2 gap-6">
        <div className="border-2 border-black bg-white p-6 brutal-shadow">
          <h2 className="font-display font-bold text-xl">Pick Go Convert Documents if…</h2>
          <ul className="mt-3 space-y-2">
            {c.whenToPick.us.map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-zinc-800">
                <CheckCircle weight="fill" className="text-emerald-600 mt-0.5 shrink-0" size={18} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border-2 border-black bg-white p-6 brutal-shadow-sm">
          <h2 className="font-display font-bold text-xl">Pick {c.competitor} if…</h2>
          <ul className="mt-3 space-y-2">
            {c.whenToPick.them.map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-zinc-800">
                <XCircle weight="fill" className="text-rose-600 mt-0.5 shrink-0" size={18} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-12 border-2 border-black bg-yellow-100 p-6 brutal-shadow">
        <p className="font-mono-plex text-xs uppercase tracking-widest text-zinc-600">Try it free</p>
        <h3 className="font-display font-bold text-2xl mt-2">
          Ready to convert? No signup, unlimited, private.
        </h3>
        <Link
          to="/"
          data-testid="compare-cta"
          className="brutal-btn inline-flex items-center gap-2 mt-4"
        >
          Open the toolbox <ArrowRight weight="bold" size={18} />
        </Link>
      </div>

      {others.length > 0 && (
        <section className="mt-16 border-t-2 border-black pt-10">
          <h2 className="font-display font-bold text-2xl md:text-3xl">Other comparisons</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/vs/${o.slug}`}
                data-testid={`related-compare-${o.slug}`}
                className="border-2 border-black bg-white p-4 brutal-shadow-sm hover:brutal-shadow"
                style={{ transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
              >
                <p className="font-semibold text-sm">vs {o.competitor}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: safeJsonLd({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: c.title,
            description: c.description,
            keywords: c.keywords,
            mainEntityOfPage: `${SITE}/vs/${c.slug}`,
            author: { '@type': 'Organization', name: 'Go Convert Documents' },
            publisher: {
              '@type': 'Organization',
              name: 'Go Convert Documents',
              logo: { '@type': 'ImageObject', url: `${SITE}/logo.png` },
            },
          }),
        }}
      />
    </div>
  );
}
